/**
 * Pricing Section
 * Tiered pricing cards
 */

import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { staggerContainerVariants, itemVariants, cardHoverVariants } from '@/components/animations/variants';

const pricingTiers = [
  {
    name: 'Spark',
    price: '$0',
    period: '/month',
    limit: '50K requests/mo • 5.5% usage fee',
    features: [
      'Basic routing (3 providers)',
      'Routing only — upgrade to unlock caching + compression',
      'Community support',
    ],
    cta: 'Start Spark',
    variant: 'outline' as const,
    popular: false,
  },
  {
    name: 'Launch',
    price: '$19',
    period: '/month',
    limit: '200K requests/mo • 5.5% usage fee',
    features: [
      '5K cached responses/mo',
      '10K compressed requests/mo',
      'Analytics + usage alerts',
      'Email support',
    ],
    cta: 'Start Launch',
    variant: 'outline' as const,
    popular: false,
  },
  {
    name: 'Scale',
    price: '$49',
    period: '/month',
    limit: '2M requests/mo • 5.5% usage fee',
    features: [
      '50K cached responses/mo',
      'Unlimited compression',
      'Advanced routing policies',
      'Cost reports + team access',
    ],
    cta: 'Start Scale',
    variant: 'default' as const,
    popular: true,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    limit: 'Unlimited usage • 5.5% usage fee',
    features: [
      'Unlimited caching + compression',
      'Custom routing policies',
      'SLA guarantees',
      'Dedicated support',
      'Private deployments',
    ],
    cta: 'Contact Sales',
    variant: 'outline' as const,
    popular: false,
  },
];

export function PricingSection() {
  return (
    <section className="py-16 px-4" id="pricing">
      <div className="max-w-7xl mx-auto">
        {/* Pricing Grid */}
        <motion.div
          className="grid md:grid-cols-2 xl:grid-cols-4 gap-8 mb-12"
          variants={staggerContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {pricingTiers.map((tier) => (
            <motion.div
              key={tier.name}
              variants={itemVariants}
              whileHover="hover"
              initial="rest"
            >
              <motion.div
                className={`backdrop-blur-md bg-background/50 border rounded-xl p-8 text-center h-full flex flex-col shadow-xl ${
                  tier.popular ? 'border-primary' : 'border-border/50'
                }`}
                variants={cardHoverVariants}
              >
                {/* Popular Badge */}
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-medium">
                      Popular
                    </span>
                  </div>
                )}

                {/* Tier Name */}
                <h3 className="text-2xl font-bold mb-2">{tier.name}</h3>

                {/* Price */}
                <div className="mb-4">
                  <span className="text-4xl font-bold">{tier.price}</span>
                  <span className="text-lg text-muted-foreground">{tier.period}</span>
                </div>

                {/* Limit */}
                <div className="text-muted-foreground mb-6">{tier.limit}</div>

                {/* Features */}
                <ul className="space-y-3 mb-8 text-left flex-grow">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-center space-x-2">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <div>
                  <Button
                    variant={tier.variant}
                    className={`w-full ${
                      tier.popular
                        ? 'bg-accent hover:bg-accent/90 text-accent-foreground'
                        : ''
                    }`}
                  >
                    {tier.cta}
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom Note */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
        >
          <p className="text-muted-foreground mb-4">
            💡 Provider prices pass through. We add a 5.5% usage fee on usage.
          </p>
          <p className="text-sm text-muted-foreground mb-6">
            Overage model: caching and compression continue beyond limits with per-request fees.
          </p>
          <Button variant="default">See Full Pricing Details →</Button>
        </motion.div>
      </div>
    </section>
  );
}
